import { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { GripVertical, Pencil, Check, X, Plus, Trash2 } from 'lucide-react';
import { useStages, useCreateStage, useUpdateStage, useDeleteStage, useReorderStages } from '../../hooks/useStages.js';
import { toast } from '../../lib/toast.js';
import { useWorkspaceStore } from '../../stores/workspace.js';
import { useFeatureFlag } from '../../lib/posthog.js';
import type { Stage } from '@mayksel/types';

const PRESET_COLORS = [
  '#6366f1', '#8b5cf6', '#ec4899', '#ef4444',
  '#f97316', '#eab308', '#22c55e', '#14b8a6',
  '#06b6d4', '#3b82f6', '#64748b', '#1f2937',
];

interface EditState {
  name: string;
  color: string;
}

export default function StagesSettings() {
  const { t } = useTranslation('settings');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const stagesEnabled = useFeatureFlag('stages');
  const { data: stages = [], isLoading } = useStages();

  const createStage = useCreateStage();
  const deleteStage = useDeleteStage();
  const reorderStages = useReorderStages();

  // New stage form
  const [newName, setNewName] = useState('');
  const [newColor, setNewColor] = useState('#6366f1');
  const [showNewColorPicker, setShowNewColorPicker] = useState(false);

  // Per-row inline editing
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editState, setEditState] = useState<EditState>({ name: '', color: '' });
  const [showEditColorPicker, setShowEditColorPicker] = useState(false);

  // Drag state
  const dragId = useRef<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const updateStage = useUpdateStage(editingId ?? '');

  const orderLabel = workspace?.orderLabel ?? 'Order';
  const orderLabelPlural = workspace?.orderLabelPlural ?? 'Orders';

  async function handleCreate() {
    if (!newName.trim()) return;
    try {
      await createStage.mutateAsync({ name: newName.trim(), color: newColor, isDefault: false, isComplete: false });
      setNewName('');
      setNewColor('#6366f1');
    } catch {
      toast.error(t('stages.create_error'));
    }
  }

  function startEdit(stage: Stage) {
    setEditingId(stage.id);
    setEditState({ name: stage.name, color: stage.color });
    setShowEditColorPicker(false);
  }

  async function saveEdit() {
    if (!editingId) return;
    try {
      await updateStage.mutateAsync({ name: editState.name.trim(), color: editState.color });
      setEditingId(null);
    } catch {
      toast.error(t('stages.save_error'));
    }
  }

  function cancelEdit() {
    setEditingId(null);
    setShowEditColorPicker(false);
  }

  async function handleDelete(stage: Stage) {
    if (!confirm(t('stages.delete_confirm', { name: stage.name }))) return;
    try {
      await deleteStage.mutateAsync(stage.id);
    } catch {
      toast.error(t('stages.delete_error'));
    }
  }

  async function handleToggleDefault(stage: Stage) {
    const updateSingle = useUpdateStageById(stage.id);
    try {
      await updateSingle({ isDefault: !stage.isDefault });
    } catch {
      toast.error(t('stages.save_error'));
    }
  }

  async function handleToggleComplete(stage: Stage) {
    const updateSingle = useUpdateStageById(stage.id);
    try {
      await updateSingle({ isComplete: !stage.isComplete });
    } catch {
      toast.error(t('stages.save_error'));
    }
  }

  // Drag-and-drop reorder
  function onDragStart(id: string) {
    dragId.current = id;
  }

  function onDragOver(e: React.DragEvent, overId: string) {
    e.preventDefault();
    setDragOverId(overId);
  }

  async function onDrop(e: React.DragEvent, overId: string) {
    e.preventDefault();
    setDragOverId(null);
    const fromId = dragId.current;
    if (!fromId || fromId === overId) return;

    const ids = stages.map((s) => s.id);
    const fromIdx = ids.indexOf(fromId);
    const toIdx = ids.indexOf(overId);
    if (fromIdx === -1 || toIdx === -1) return;

    const reordered = [...ids];
    reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, fromId);

    try {
      await reorderStages.mutateAsync(reordered);
    } catch {
      toast.error(t('stages.reorder_error'));
    }
  }

  if (!stagesEnabled) return null;

  if (isLoading) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        {Array.from({ length: 4 }).map((_, i) => (
          <div key={i} className="skeleton-line" style={{ height: 48 }} />
        ))}
      </div>
    );
  }

  return (
    <div>
      <div style={{ marginBottom: 'var(--space-6)' }}>
        <h2 style={{ fontSize: 'var(--text-xl)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-1)' }}>
          {t('stages.title')}
        </h2>
        <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>
          {t('stages.subtitle', { order_label_plural: orderLabelPlural })}
        </p>
      </div>

      {/* Stage list */}
      <div style={{
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
        overflow: 'visible', marginBottom: 'var(--space-6)',
      }}>
        {stages.length === 0 ? (
          <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
            <p style={{ fontSize: 'var(--text-sm)', color: 'var(--color-text-muted)' }}>{t('stages.no_stages')}</p>
          </div>
        ) : stages.map((stage, idx) => (
          <div
            key={stage.id}
            draggable
            onDragStart={() => onDragStart(stage.id)}
            onDragOver={(e) => onDragOver(e, stage.id)}
            onDragEnd={() => { dragId.current = null; setDragOverId(null); }}
            onDrop={(e) => void onDrop(e, stage.id)}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-3)',
              padding: 'var(--space-3) var(--space-4)',
              borderBottom: idx < stages.length - 1 ? '1px solid var(--color-gray-100)' : 'none',
              background: dragOverId === stage.id ? 'var(--color-brand-pale)' : 'var(--color-bg)',
              transition: 'background 100ms',
            }}
          >
            <GripVertical size={16} style={{ color: 'var(--color-gray-300)', flexShrink: 0, cursor: 'grab' }} />

            {editingId === stage.id ? (
              /* Edit row */
              <>
                {/* Color swatch + picker */}
                <div style={{ position: 'relative', flexShrink: 0 }}>
                  <button
                    type="button"
                    onClick={() => setShowEditColorPicker((v) => !v)}
                    style={{
                      width: 28, height: 28, borderRadius: 'var(--radius-md)',
                      background: editState.color, border: '2px solid var(--color-border)',
                      cursor: 'pointer', flexShrink: 0,
                    }}
                    aria-label="Choose color"
                  />
                  {showEditColorPicker && (
                    <ColorPicker
                      value={editState.color}
                      onChange={(c) => setEditState((s) => ({ ...s, color: c }))}
                      onClose={() => setShowEditColorPicker(false)}
                    />
                  )}
                </div>

                <input
                  type="text" value={editState.name} autoFocus
                  onChange={(e) => setEditState((s) => ({ ...s, name: e.target.value }))}
                  onKeyDown={(e) => { if (e.key === 'Enter') void saveEdit(); if (e.key === 'Escape') cancelEdit(); }}
                  style={{
                    flex: 1, padding: 'var(--space-1) var(--space-2)',
                    border: '1px solid var(--color-brand)', borderRadius: 'var(--radius-sm)',
                    fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)',
                  }}
                />

                <button type="button" onClick={() => void saveEdit()}
                  disabled={updateStage.isPending}
                  aria-label="Save"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-success)', display: 'flex', padding: 4 }}>
                  <Check size={16} />
                </button>
                <button type="button" onClick={cancelEdit}
                  aria-label="Cancel"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex', padding: 4 }}>
                  <X size={16} />
                </button>
              </>
            ) : (
              /* View row */
              <>
                <span style={{
                  width: 12, height: 12, borderRadius: '50%', flexShrink: 0,
                  background: stage.color,
                }} />
                <span style={{ flex: 1, fontSize: 'var(--text-sm)', color: 'var(--color-text)' }}>
                  {stage.name}
                </span>

                {/* Badges */}
                <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                  <Badge
                    label={t('stages.default_label')}
                    active={stage.isDefault}
                    title={t('stages.set_default')}
                    activeColor="var(--color-brand)"
                    onClick={() => void handleToggleDefault(stage)}
                  />
                  <Badge
                    label={t('stages.complete_label')}
                    active={stage.isComplete}
                    title={t('stages.set_complete')}
                    activeColor="var(--color-success)"
                    onClick={() => void handleToggleComplete(stage)}
                  />
                </div>

                <button type="button" onClick={() => startEdit(stage)}
                  aria-label={`Edit ${stage.name}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex', padding: 4 }}>
                  <Pencil size={14} />
                </button>
                <button type="button" onClick={() => void handleDelete(stage)}
                  aria-label={`Delete ${stage.name}`}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-300)', display: 'flex', padding: 4 }}>
                  <Trash2 size={14} />
                </button>
              </>
            )}
          </div>
        ))}
      </div>

      {/* Add new stage */}
      <div style={{
        border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
      }}>
        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)', marginBottom: 'var(--space-3)' }}>
          {t('stages.add_stage')}
        </p>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)' }}>
          {/* Color picker */}
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <button
              type="button"
              onClick={() => setShowNewColorPicker((v) => !v)}
              style={{
                width: 36, height: 36, borderRadius: 'var(--radius-md)',
                background: newColor, border: '2px solid var(--color-border)',
                cursor: 'pointer',
              }}
              aria-label="Choose color"
            />
            {showNewColorPicker && (
              <ColorPicker
                value={newColor}
                onChange={setNewColor}
                onClose={() => setShowNewColorPicker(false)}
              />
            )}
          </div>

          <input
            type="text" value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && void handleCreate()}
            placeholder={t('stages.name_placeholder')}
            style={{
              flex: 1, padding: 'var(--space-2) var(--space-3)',
              border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
              fontSize: 'var(--text-sm)', color: 'var(--color-text)', background: 'var(--color-bg)',
            }}
          />

          <button
            type="button"
            onClick={() => void handleCreate()}
            disabled={!newName.trim() || createStage.isPending}
            style={{
              display: 'flex', alignItems: 'center', gap: 'var(--space-1)',
              padding: 'var(--space-2) var(--space-4)',
              background: newName.trim() ? 'var(--color-brand)' : 'var(--color-bg)',
              color: newName.trim() ? 'var(--color-text-inverse)' : 'var(--color-text-muted)',
              border: newName.trim() ? 'none' : '1px solid var(--color-border)',
              borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-medium)', cursor: newName.trim() ? 'pointer' : 'not-allowed',
              whiteSpace: 'nowrap',
            }}
          >
            <Plus size={14} />
            {t('stages.add_stage')}
          </button>
        </div>

        {/* Color presets */}
        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)', flexWrap: 'wrap' }}>
          {PRESET_COLORS.map((c) => (
            <button
              key={c} type="button"
              onClick={() => { setNewColor(c); setShowNewColorPicker(false); }}
              style={{
                width: 20, height: 20, borderRadius: '50%', background: c,
                border: newColor === c ? '2px solid var(--color-text)' : '2px solid transparent',
                cursor: 'pointer', padding: 0,
              }}
              aria-label={c}
            />
          ))}
        </div>
      </div>

      <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 'var(--space-4)' }}>
        {orderLabel} counts: {stages.length} stage{stages.length !== 1 ? 's' : ''} configured
      </p>
    </div>
  );
}

// ── Helpers ────────────────────────────────────────────────────────────────────

// Hook that returns an updater for a specific stage ID (inline to avoid hook rules issues)
function useUpdateStageById(stageId: string) {
  const update = useUpdateStage(stageId);
  return update.mutateAsync.bind(update);
}

function Badge({ label, active, title, activeColor, onClick }: {
  label: string; active: boolean; title: string; activeColor: string; onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      style={{
        padding: '2px var(--space-2)',
        borderRadius: 'var(--radius-full)',
        fontSize: 'var(--text-xs)',
        fontWeight: active ? 'var(--font-medium)' : undefined,
        color: active ? activeColor : 'var(--color-text-muted)',
        background: 'none',
        border: `1px solid ${active ? activeColor : 'var(--color-border)'}`,
        cursor: 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </button>
  );
}

function ColorPicker({ value, onChange, onClose }: {
  value: string; onChange: (c: string) => void; onClose: () => void;
}) {
  return (
    <div
      style={{
        position: 'absolute', top: '100%', left: 0, zIndex: 50,
        marginTop: 4, padding: 'var(--space-3)',
        background: 'var(--color-bg)', border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)', boxShadow: 'var(--shadow-lg)',
        width: 168,
      }}
    >
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        {PRESET_COLORS.map((c) => (
          <button
            key={c} type="button"
            onMouseDown={() => { onChange(c); onClose(); }}
            style={{
              width: 24, height: 24, borderRadius: '50%', background: c,
              border: value === c ? '2px solid var(--color-text)' : '2px solid transparent',
              cursor: 'pointer', padding: 0,
            }}
            aria-label={c}
          />
        ))}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
        <input
          type="color" value={value}
          onChange={(e) => onChange(e.target.value)}
          style={{ width: 32, height: 28, border: 'none', background: 'none', cursor: 'pointer', padding: 0 }}
        />
        <input
          type="text" value={value}
          onChange={(e) => /^#[0-9a-fA-F]{0,6}$/.test(e.target.value) && onChange(e.target.value)}
          style={{
            flex: 1, padding: '4px var(--space-2)',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)',
            fontSize: 'var(--text-xs)', color: 'var(--color-text)', background: 'var(--color-bg)',
            fontFamily: 'var(--font-mono)',
          }}
        />
      </div>
    </div>
  );
}
